import { type FC, useMemo } from 'react';
import type { DisplayType } from '../types';
import {
  HistoricalYearsTooltip,
  type SalesSummaryProperties,
  SummaryHeader,
  SummaryTile,
  useImportMonth,
} from './index';

export const MarginSummary: FC<SalesSummaryProperties> = ({
  lastImported,
  thisYearSales,
  thisYearEstimates,
  historicalYears,
}) => {
  const displayType: DisplayType = 'Gross Margin';
  const thisYear = useMemo(() => thisYearSales.year, [thisYearSales]);
  const lastYear = useMemo(() => thisYear - 1, [thisYear]);
  const lastYearSales = useMemo(() => historicalYears[0], [historicalYears]);

  const { index, monthName, isMtdCurrent } = useImportMonth(lastImported);
  const thisYearMonth = useMemo(() => thisYearSales.monthly[index], [thisYearSales, index]);
  const lastYearMtdAmount = useMemo(() => {
    return (
      lastYearSales &&
      lastYearSales.totals_to_date_month.sales - lastYearSales.totals_to_date_month.costs
    );
  }, [lastYearSales]);

  const mtdAmount = useMemo(() => {
    return thisYearMonth.sales - thisYearMonth.costs;
  }, [thisYearMonth]);

  const mtdPercent = useMemo(() => {
    if (thisYearMonth.sales === 0) {
      return 0;
    }

    return mtdAmount / thisYearMonth.sales;
  }, [mtdAmount, thisYearMonth]);

  const lastYearMtdPercent = useMemo(() => {
    return (
      lastYearMtdAmount &&
      lastYearSales &&
      lastYearMtdAmount / lastYearSales.totals_to_date_month.sales
    );
  }, [lastYearMtdAmount, lastYearSales]);

  const thisYearTotalEstimate = useMemo(() => {
    return thisYearEstimates.totals.sales - thisYearEstimates.totals.costs;
  }, [thisYearEstimates]);

  const ytdAmount = useMemo(() => {
    return thisYearSales.totals_to_date.sales - thisYearSales.totals_to_date.costs;
  }, [thisYearSales]);

  const ytdPercent = useMemo(() => {
    return ytdAmount / thisYearSales.totals_to_date.sales;
  }, [ytdAmount, thisYearSales]);

  const lastYearYtdAmount = useMemo(() => {
    return lastYearSales && lastYearSales.totals_to_date.sales - lastYearSales.totals_to_date.costs;
  }, [lastYearSales]);

  const lastYearYtdPercent = useMemo(() => {
    return (
      lastYearYtdAmount && lastYearSales && lastYearYtdAmount / lastYearSales.totals_to_date.sales
    );
  }, [lastYearYtdAmount, lastYearSales]);

  const thisMonthEstimate = useMemo(() => {
    return thisYearEstimates.monthly[index].sales - thisYearEstimates.monthly[index].costs;
  }, [thisYearEstimates, index]);

  const historicalTooltipContent = useMemo(() => {
    if (!historicalYears || historicalYears.length === 0 || !displayType) {
      return undefined;
    }

    return <HistoricalYearsTooltip historicalYears={historicalYears} displayType={displayType} />;
  }, [historicalYears]);

  return (
    <div className="grid grid-flow-row">
      <SummaryHeader>{monthName}</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} MTD $`}
          current={lastYearMtdAmount}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYear} MTD $`}
          current={isMtdCurrent ? mtdAmount : undefined}
          previous={isMtdCurrent ? lastYearMtdAmount : undefined}
          timespan="thisYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYear} MTD %`}
          current={isMtdCurrent ? mtdPercent : undefined}
          previous={isMtdCurrent ? lastYearMtdPercent : undefined}
          timespan="thisYear"
          format="percentage"
        />
        <SummaryTile
          title={`${thisYear} Total ~`}
          current={thisMonthEstimate}
          timespan="thisYearEstimates"
          format="currency"
        />
      </div>
      <SummaryHeader>Year</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} YTD $`}
          current={lastYearYtdAmount}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYear} YTD $`}
          current={ytdAmount}
          previous={lastYearYtdAmount}
          timespan="thisYear"
          format="currency"
          tooltipContent={historicalTooltipContent}
        />
        <SummaryTile
          title={`${thisYear} YTD %`}
          current={ytdPercent}
          previous={lastYearYtdPercent}
          timespan="thisYear"
          format="percentage"
        />
        <SummaryTile
          title={`${thisYear} Total ~`}
          current={thisYearTotalEstimate}
          timespan="thisYearEstimates"
          format="currency"
        />
      </div>
    </div>
  );
};
